import React, { useState, useMemo } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import './HistoricalChart.css';

const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#06B6D4', // Cyan
];

const RANGE_OPTIONS = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: 'All', days: null },
];

function formatYAxis(value) {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`;
  return `€${value}`;
}

function formatXAxis(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const date = new Date(label);
  const dateLabel = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const total = payload.find(p => p.dataKey === '__total__');
  const lines = payload.filter(p => p.dataKey !== '__total__');

  return (
    <div className="hc-tooltip">
      <div className="hc-tooltip-date">{dateLabel}</div>
      {total && (
        <div className="hc-tooltip-total">
          <span>Total</span>
          <span>€{Math.round(total.value).toLocaleString()}</span>
        </div>
      )}
      {lines.map((entry) => (
        <div key={entry.dataKey} className="hc-tooltip-row">
          <span className="hc-tooltip-dot" style={{ background: entry.color }} />
          <span className="hc-tooltip-name">{entry.name}</span>
          <span className="hc-tooltip-val">€{Math.round(entry.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function HistoricalChart({ investments, selectedTypes }) {
  const [activeRange, setActiveRange] = useState('All');
  const [hiddenLines, setHiddenLines] = useState({});

  const filteredInvestments = useMemo(() => {
    return selectedTypes.length > 0
      ? investments.filter(inv => selectedTypes.includes(inv.type))
      : investments;
  }, [investments, selectedTypes]);

  // Top 5 investments by current EUR value
  const top5 = useMemo(() => {
    return [...filteredInvestments]
      .sort((a, b) => (b.eurValue || 0) - (a.eurValue || 0))
      .slice(0, 5);
  }, [filteredInvestments]);

  const chartData = useMemo(() => {
    if (filteredInvestments.length === 0) return [];

    // Collect all unique dates
    const allDates = new Set();
    filteredInvestments.forEach(inv => {
      (inv.valueHistory || []).forEach(h => {
        allDates.add(new Date(h.timeOfUpdate).toISOString().split('T')[0]);
      });
    });

    // Apply date range filter
    const rangeOption = RANGE_OPTIONS.find(r => r.label === activeRange);
    const cutoff = rangeOption?.days
      ? new Date(Date.now() - rangeOption.days * 86400000)
      : null;

    const sortedDates = Array.from(allDates)
      .sort()
      .filter(d => !cutoff || new Date(d) >= cutoff);

    if (sortedDates.length === 0) return [];

    return sortedDates.map(date => {
      const row = { date };

      // Per-investment value (last known value on or before this date)
      top5.forEach(inv => {
        const latest = (inv.valueHistory || [])
          .filter(h => new Date(h.timeOfUpdate).toISOString().split('T')[0] <= date)
          .sort((a, b) => new Date(b.timeOfUpdate) - new Date(a.timeOfUpdate))[0];
        row[inv.name] = latest ? Math.round(latest.eurValue) : 0;
      });

      // Total across ALL filtered investments
      let total = 0;
      filteredInvestments.forEach(inv => {
        const latest = (inv.valueHistory || [])
          .filter(h => new Date(h.timeOfUpdate).toISOString().split('T')[0] <= date)
          .sort((a, b) => new Date(b.timeOfUpdate) - new Date(a.timeOfUpdate))[0];
        if (latest) total += latest.eurValue;
      });
      row.__total__ = Math.round(total);

      return row;
    });
  }, [filteredInvestments, top5, activeRange]);

  const startValue = chartData.length > 0 ? chartData[0].__total__ : null;

  const handleLegendClick = (e) => {
    const key = e.dataKey;
    setHiddenLines(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (chartData.length === 0) {
    return (
      <div className="hc-empty">No history data available for selected filters</div>
    );
  }

  return (
    <div className="hc-wrapper">
      {/* Date range selector */}
      <div className="hc-range-bar">
        {RANGE_OPTIONS.map(opt => (
          <button
            key={opt.label}
            className={`hc-range-btn${activeRange === opt.label ? ' active' : ''}`}
            onClick={() => setActiveRange(opt.label)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={500}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />

          <XAxis
            dataKey="date"
            tickFormatter={formatXAxis}
            tick={{ fontSize: 12, fill: '#888' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            minTickGap={60}
          />

          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 12, fill: '#888' }}
            tickLine={false}
            axisLine={false}
            width={70}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            onClick={handleLegendClick}
            wrapperStyle={{ cursor: 'pointer', paddingTop: '12px', fontSize: '13px' }}
          />

          {/* Reference line at portfolio start value */}
          {startValue && (
            <ReferenceLine
              y={startValue}
              stroke="#d1d5db"
              strokeDasharray="4 4"
              label={{ value: 'Start', position: 'insideTopLeft', fontSize: 11, fill: '#9ca3af' }}
            />
          )}

          {/* Total portfolio — filled area (bottom layer) */}
          <Area
            type="monotone"
            dataKey="__total__"
            name="Total Portfolio"
            fill="rgba(102, 126, 234, 0.12)"
            stroke="rgba(102, 126, 234, 0.9)"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5 }}
            hide={hiddenLines['__total__']}
          />

          {/* Individual investment lines */}
          {top5.map((inv, i) => (
            <Line
              key={inv.name}
              type="monotone"
              dataKey={inv.name}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={1.8}
              dot={false}
              activeDot={{ r: 4 }}
              hide={hiddenLines[inv.name]}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default HistoricalChart;
